import React, { useEffect, useState } from 'react';
import { useData } from '../contexts/DataContext';
import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyDollarIcon,
  UsersIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';

const Analytics = () => {
  const { analytics, orders, customers, loading, refreshData } = useData();
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('30'); // days
  // Track small (sub-`sm`, <640px) viewports so charts can hide/shrink axis labels on phones.
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(max-width: 639px)');
    const onChange = (e) => setIsSmallScreen(e.matches);
    setIsSmallScreen(mql.matches);
    // addEventListener is the modern API; addListener is the Safari/legacy fallback.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshData();
    setRefreshing(false);
  };

  // Use real analytics data from your database
  const revenueData = analytics?.monthlyRevenue?.map(item => ({
    month: new Date(item.month).toLocaleDateString('en-US', { month: 'short' }),
    revenue: parseFloat(item.revenue || 0),
    orders: parseInt(item.order_count || 0)
  })) || [];

  const topCarriers = analytics?.carrierStats?.map(item => ({
    name: item.carrier || 'Unknown',
    shipments: parseInt(item.shipment_count || 0),
    percentage: parseFloat(item.percentage || 0)
  })) || [];

  const orderStatusData = [
    { name: 'Delivered', value: 45, color: '#10B981' },
    { name: 'In Transit', value: 30, color: '#F59E0B' },
    { name: 'Processing', value: 20, color: '#3B82F6' },
    { name: 'Cancelled', value: 5, color: '#EF4444' },
  ];

  const customerAcquisitionData = [
    { month: 'Jan', newCustomers: 23, returning: 67 },
    { month: 'Feb', newCustomers: 31, returning: 78 },
    { month: 'Mar', newCustomers: 28, returning: 82 },
    { month: 'Apr', newCustomers: 35, returning: 91 },
    { month: 'May', newCustomers: 42, returning: 95 },
    { month: 'Jun', newCustomers: 38, returning: 103 },
  ];

  const timeRanges = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 3 months' },
    { value: '365', label: 'Last year' },
  ];

  const kpiCards = [
    {
      title: 'Total Revenue',
      value: `$${(analytics?.totalRevenue || 0).toLocaleString()}`,
      change: '+12.5%',
      trend: 'up',
      icon: CurrencyDollarIcon,
    },
    {
      title: 'Active Customers',
      value: analytics?.totalCustomers || 0,
      change: '+8.2%',
      trend: 'up',
      icon: UsersIcon,
    },
    {
      title: 'Rate Request Success',
      value: `${analytics?.rateRequestStats?.success_rate || 0}%`,
      change: analytics?.rateRequestStats?.success_rate > 90 ? '+2.1%' : '-2.1%',
      trend: analytics?.rateRequestStats?.success_rate > 90 ? 'up' : 'down',
      icon: ArrowTrendingUpIcon,
    },
  ];

  if (loading && !customers && !orders) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
        <div className="flex items-center space-x-4">
          <select
            className="block w-40 pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            onChange={(e) => setTimeRange(e.target.value)}
            value={timeRange}
          >
            {timeRanges.map((range) => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </select>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <ArrowPathIcon className={`-ml-1 mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards.map((kpi) => (
          <div key={kpi.title} className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <kpi.icon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">{kpi.title}</dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">{kpi.value}</div>
                      <div
                        className={`ml-2 flex items-baseline text-sm font-semibold ${
                          kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {kpi.trend === 'up' ? (
                          <ArrowTrendingUpIcon className="self-center flex-shrink-0 h-4 w-4" />
                        ) : (
                          <ArrowTrendingDownIcon className="self-center flex-shrink-0 h-4 w-4" />
                        )}
                        <span className="ml-1">{kpi.change}</span>
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue and Orders Chart */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue & Orders Over Time</h3>
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={revenueData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => new Date(value).toLocaleDateString()}
              tick={{ fontSize: isSmallScreen ? 10 : 12 }}
              interval={isSmallScreen ? 'preserveStartEnd' : 0}
            />
            <YAxis yAxisId="left" tick={{ fontSize: isSmallScreen ? 10 : 12 }} width={isSmallScreen ? 36 : 60} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: isSmallScreen ? 10 : 12 }} width={isSmallScreen ? 36 : 60} />
            <Tooltip 
              labelFormatter={(value) => new Date(value).toLocaleDateString()}
              formatter={(value, name) => [
                name === 'revenue' ? `$${value.toLocaleString()}` : value,
                name === 'revenue' ? 'Revenue' : 'Orders'
              ]}
            />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="revenue"
              stackId="1"
              stroke="#3B82F6"
              fill="#3B82F6"
              fillOpacity={0.6}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="orders"
              stroke="#F59E0B"
              strokeWidth={2}
              dot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Carriers */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Shipping Carriers</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topCarriers} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: isSmallScreen ? 10 : 12 }} />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: isSmallScreen ? 10 : 12 }}
                width={isSmallScreen ? 56 : 80}
              />
              <Tooltip formatter={(value) => [value, 'Shipments']} />
              <Bar dataKey="shipments" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Order Status Distribution */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Order Status Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={orderStatusData}
                cx="50%"
                cy="50%"
                outerRadius={isSmallScreen ? 64 : 80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) =>
                  isSmallScreen
                    ? `${(percent * 100).toFixed(0)}%`
                    : `${name} ${(percent * 100).toFixed(0)}%`
                }
              >
                {orderStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Customer Acquisition */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Acquisition</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={customerAcquisitionData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: isSmallScreen ? 10 : 12 }} />
              <YAxis tick={{ fontSize: isSmallScreen ? 10 : 12 }} width={isSmallScreen ? 32 : 60} />
              <Tooltip />
              <Bar dataKey="newCustomers" stackId="a" fill="#3B82F6" name="New Customers" />
              <Bar dataKey="returning" stackId="a" fill="#10B981" name="Returning Customers" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Shipping Performance */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Shipping Performance Metrics</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-500">Total Rate Requests</span>
              <span className="text-sm font-bold text-gray-900">
                {analytics?.rateRequestStats?.total_requests || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-500">Successful Requests</span>
              <span className="text-sm font-bold text-green-600">
                {analytics?.rateRequestStats?.successful_requests || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-500">Success Rate</span>
              <span className={`text-sm font-bold ${
                (analytics?.rateRequestStats?.success_rate || 0) > 90 ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {analytics?.rateRequestStats?.success_rate || 0}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-500">Top Carrier</span>
              <span className="text-sm font-bold text-gray-900">
                {topCarriers[0]?.name || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-500">Average Order Value</span>
              <span className="text-sm font-bold text-green-600">
                ${analytics?.totalRevenue && analytics?.totalOrders 
                  ? (analytics.totalRevenue / analytics.totalOrders).toFixed(2)
                  : '0.00'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Customers Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Top Customers (by Revenue)</h3>
        </div>
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Orders
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Spent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics?.topCustomers?.length > 0 ? (
                analytics.topCustomers.map((customer, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {customer.name || 'Unknown Customer'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {customer.order_count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-semibold">
                      ${parseFloat(customer.total_spent || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {customer.email || 'N/A'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                    No customer data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile stacked cards */}
        <div className="md:hidden p-4 space-y-3">
          {analytics?.topCustomers?.length > 0 ? (
            analytics.topCustomers.map((customer, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="font-medium text-gray-900 break-words">
                  {customer.name || 'Unknown Customer'}
                </div>
                <p className="text-sm text-gray-500 break-all mt-1">
                  {customer.email || 'N/A'}
                </p>
                <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                  <div>
                    <span className="text-gray-400 block text-xs uppercase tracking-wider">Total Orders</span>
                    <span className="text-gray-900">{customer.order_count || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block text-xs uppercase tracking-wider">Total Spent</span>
                    <span className="font-semibold text-gray-900">
                      ${parseFloat(customer.total_spent || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-sm text-gray-500 py-4">
              No customer data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
